import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchFunnel } from '../../lib/funnelPublicApi';
import FunnelLayout from './FunnelLayout';
import FunnelNotFound from './FunnelNotFound';
import FunnelError from './FunnelError';

function setMetaTag(attr, key, value) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function FunnelLoading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-3">
      <div
        className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-[#00AAFF] animate-spin"
        role="status"
        aria-label="Loading"
      />
      <p className="text-sm text-gray-500">Loading…</p>
    </div>
  );
}

export default function FunnelPage() {
  const { slug, step: stepParam } = useParams();
  const [funnel, setFunnel] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    setNotFound(false);

    try {
      const result = await fetchFunnel(slug, stepParam || null);
      if (result.notFound) {
        setNotFound(true);
        setFunnel(null);
        return;
      }
      if (!result.ok) {
        setError(true);
        return;
      }

      const { funnel: funnelData, current_step, blocks: blockList, steps: stepList } = result.data;
      setFunnel(funnelData);
      setCurrentStep(current_step);
      setBlocks(blockList ?? []);
      setSteps(stepList ?? []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [slug, stepParam]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!funnel || !currentStep) return;

    const settings = funnel.settings || {};
    const pageTitle = [funnel.name, currentStep.title].filter(Boolean).join(' — ');
    document.title = pageTitle || 'Prosperity Leaders';

    const ogTitle = settings.og_title || settings.meta_title || pageTitle;
    const ogDescription =
      settings.og_description || settings.meta_description || funnel.name || '';

    setMetaTag('property', 'og:title', ogTitle);
    setMetaTag('property', 'og:description', ogDescription);
    setMetaTag('name', 'description', ogDescription);
  }, [funnel, currentStep]);

  if (loading) return <FunnelLoading />;
  if (notFound) return <FunnelNotFound />;
  if (error) return <FunnelError onRetry={load} />;
  if (!funnel || !currentStep) return <FunnelNotFound />;

  return (
    <FunnelLayout
      funnel={funnel}
      currentStep={currentStep}
      blocks={blocks}
      steps={steps}
      slug={slug}
    />
  );
}
